<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Weekly Breakup</h5>
          <small class="text-muted"
            >Manage weekly topics by class, subject &amp; date range</small
          >
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              class="page-size-select-compact"
              @input="onPageSizeChange"
              append-to-body
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadItems"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--cols-4 d-flex align-items-center"
          >
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input"
              :placeholder="$t('Search') + '...'"
              @input="onSearchChange"
            />
            <v-select
              v-model="filters.classID"
              :options="classes"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Class"
              class="filter-control filter-field-item"
              @input="onFilterClassChange"
              append-to-body
            />
            <v-select
              v-model="filters.sectionID"
              :options="filterSections"
              :clearable="true"
              multiple
              :close-on-select="false"
              label="section"
              :reduce="(val) => val.id"
              placeholder="Section"
              :disabled="!filters.classID"
              class="filter-control filter-field-item"
              @input="onFilterChange"
              append-to-body
            />
            <v-select
              v-model="filters.subjectID"
              :options="filterSubjects"
              :clearable="true"
              label="subject"
              :reduce="(val) => val.id"
              placeholder="Subject"
              :disabled="isTeacherScoped && !filters.classID"
              class="filter-control filter-field-item"
              @input="onFilterChange"
              append-to-body
            />
            <div class="filter-date-wrap filter-field-item">
              <flat-pickr
                v-model="filters.dtFrom"
                :config="dateConfig"
                class="form-control filter-control"
                placeholder="From"
                @on-change="onFilterChange"
              />
            </div>
            <div class="filter-date-wrap filter-field-item">
              <flat-pickr
                v-model="filters.dtTo"
                :config="dateConfig"
                class="form-control filter-control"
                placeholder="To"
                @on-change="onFilterChange"
              />
            </div>
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.view"
              variant="outline-primary"
              class="action-btn-filter add-sheet-btn text-nowrap mr-50"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              @click="openPrintModal"
            >
              <feather-icon icon="PrinterIcon" class="mr-50" />
              <span>Print</span>
            </b-button>
            <b-button
              v-if="rights.edit"
              variant="outline-success"
              class="action-btn-filter add-sheet-btn text-nowrap mr-50"
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              @click="openPublishModal"
            >
              <feather-icon icon="SendIcon" class="mr-50" />
              <span>Publish</span>
            </b-button>
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Breakup</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="items"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <div class="breakup-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="breakup-skeleton__row"
                :style="{ gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))` }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="breakup-skeleton__cell"
                >
                  <div
                    class="breakup-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(className)="data">
            <b-badge variant="light-primary">
              {{ data.value || "-" }}
            </b-badge>
          </template>

          <template #cell(sectionName)="data">
            <b-badge variant="light-info">
              {{ data.value || "-" }}
            </b-badge>
          </template>

          <template #cell(subjectName)="data">
            <b-badge variant="light-success">
              {{ data.value || "-" }}
            </b-badge>
          </template>

          <template #cell(dateRange)="data">
            <b-badge variant="light-secondary">
              {{ formatDateRange(data.item) }}
            </b-badge>
          </template>

          <template #cell(sessionName)="data">
            <b-badge variant="light-info">
              {{ data.value || getSessionName(data.item.sessionID) || "-" }}
            </b-badge>
          </template>

          <template #cell(publishDate)="data">
            <b-badge
              v-if="isItemPublished(data.item)"
              variant="light-success"
            >
              {{ formatDisplayDate(data.value || data.item.publishDate) }}
            </b-badge>
            <b-badge v-else variant="light-secondary">Not published</b-badge>
          </template>

          <template #cell(topics)="data">
            <div class="topics-preview" :title="data.value">
              {{ formatTopicsPreview(data.value) }}
            </div>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center justify-content-center">
              <b-button
                v-if="rights.view"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="View"
                size="sm"
                class="action-btn-table mr-1"
                @click="viewItem(item)"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </b-button>

              <b-button
                v-if="rights.edit"
                variant="outline-primary"
                v-b-tooltip.hover.top
                title="Edit"
                size="sm"
                class="action-btn-outline edit-btn mr-1"
                @click="editItem(item)"
              >
                <feather-icon icon="EditIcon" size="16" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                size="sm"
                class="action-btn-outline delete-btn"
                @click="deleteItem(item)"
              >
                <feather-icon icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-modal
      v-model="showModal"
      :title="modalTitle"
      size="lg"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
      modal-class="breakup-form-modal"
      backdrop-class="breakup-form-backdrop"
    >
      <div v-if="isView" class="breakup-view">
        <div class="breakup-view__meta">
          <div class="breakup-view__row">
            <span class="breakup-view__label">Class</span>
            <span class="breakup-view__value">{{ viewLabels.className }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">Section</span>
            <span class="breakup-view__value">{{ viewLabels.sectionName }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">Subject</span>
            <span class="breakup-view__value">{{ viewLabels.subjectName }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">From Date</span>
            <span class="breakup-view__value">{{ formatDisplayDate(form.dtFrom) || "-" }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">To Date</span>
            <span class="breakup-view__value">{{ formatDisplayDate(form.dtTo) || "-" }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">Session</span>
            <span class="breakup-view__value">{{ viewLabels.sessionName }}</span>
          </div>
        </div>
        <div class="breakup-view__section">
          <div class="breakup-view__section-label">Topics</div>
          <div class="breakup-view__section-text">
            {{ form.topics || "-" }}
          </div>
        </div>
      </div>

      <b-form v-else @submit.prevent>
        <b-row>
          <b-col md="4">
            <b-form-group label="Class *" label-for="wb-class">
              <v-select
                id="wb-class"
                v-model="form.classID"
                :options="classes"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select class"
                @input="onFormClassChange"
                append-to-body
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Section *" label-for="wb-section">
              <v-select
                id="wb-section"
                v-model="form.sectionID"
                :options="formSections"
                :clearable="true"
                multiple
                :close-on-select="false"
                label="section"
                :reduce="(val) => val.id"
                placeholder="Select section"
                :disabled="!form.classID"
                append-to-body
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Subject *" label-for="wb-subject">
              <v-select
                id="wb-subject"
                v-model="form.subjectID"
                :options="formSubjects"
                :clearable="false"
                label="subject"
                :reduce="(val) => val.id"
                placeholder="Select subject"
                :disabled="isTeacherScoped && !form.classID"
                append-to-body
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="From Date *" label-for="wb-from">
              <flat-pickr
                id="wb-from"
                v-model="form.dtFrom"
                :config="dateConfig"
                class="form-control"
                placeholder="From date"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="To Date *" label-for="wb-to">
              <flat-pickr
                id="wb-to"
                v-model="form.dtTo"
                :config="dateConfig"
                class="form-control"
                placeholder="To date"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Session *" label-for="wb-session">
              <v-select
                id="wb-session"
                v-model="form.sessionID"
                :options="sessions"
                :clearable="false"
                label="session"
                :reduce="(val) => val.id"
                placeholder="Select session"
                append-to-body
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Topics *" label-for="wb-topics">
              <b-form-textarea
                id="wb-topics"
                v-model="form.topics"
                placeholder="Enter topics"
                rows="6"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <template v-if="isView">
          <b-button variant="outline-secondary" @click="showModal = false">
            Close
          </b-button>
          <b-button
            v-if="rights.edit"
            variant="outline-primary"
            class="breakup-edit-outline-btn"
            @click="switchToEdit"
          >
            <feather-icon icon="EditIcon" class="mr-50" />
            Edit
          </b-button>
        </template>
        <template v-else>
          <b-button variant="outline-secondary" @click="showModal = false">
            Cancel
          </b-button>
          <b-button variant="primary" :disabled="loading" @click="saveItem">
            <b-spinner v-if="loading" small />
            {{ isEdit ? "Update" : "Save" }}
          </b-button>
        </template>
      </template>
    </b-modal>

    <b-modal
      v-model="showPublishModal"
      title="Publish Weekly Breakup"
      size="md"
      centered
      no-close-on-backdrop
      modal-class="breakup-form-modal"
      backdrop-class="breakup-form-backdrop"
      @hidden="resetPublishForm"
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="7">
            <b-form-group label="Class *" label-for="wb-pub-class">
              <v-select
                id="wb-pub-class"
                v-model="publishForm.classIDs"
                multiple
                :close-on-select="false"
                :options="printClassOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select class(es)"
                @input="onPublishClassChange"
                append-to-body
              />
            </b-form-group>
          </b-col>

          <b-col md="5">
            <b-form-group label="Section" label-for="wb-pub-section">
              <v-select
                id="wb-pub-section"
                v-model="publishForm.sectionID"
                :options="publishSections"
                :clearable="false"
                label="section"
                :reduce="(val) => val.id"
                placeholder="All sections"
                :disabled="!canSelectPublishSection"
                append-to-body
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Week From *" label-for="wb-pub-from">
              <flat-pickr
                id="wb-pub-from"
                v-model="publishForm.dtFrom"
                :config="dateConfig"
                class="form-control"
                placeholder="Monday"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Week To *" label-for="wb-pub-to">
              <flat-pickr
                id="wb-pub-to"
                v-model="publishForm.dtTo"
                :config="dateConfig"
                class="form-control"
                placeholder="Saturday"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Publish Date *" label-for="wb-pub-date">
              <flat-pickr
                id="wb-pub-date"
                v-model="publishForm.publishDate"
                :config="dateConfig"
                class="form-control"
                placeholder="Publish date"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="publishLoading"
          @click="showPublishModal = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          :disabled="publishLoading"
          @click="publishWeek"
        >
          <b-spinner v-if="publishLoading" small class="mr-50" />
          <feather-icon
            v-else
            icon="SendIcon"
            class="mr-50"
          />
          Publish
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="showPrintModal"
      title="Print Weekly Syllabus Coverage"
      centered
      no-close-on-backdrop
      modal-class="breakup-form-modal"
      backdrop-class="breakup-form-backdrop"
      @hidden="resetPrintForm"
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Class *" label-for="wb-print-class">
              <v-select
                id="wb-print-class"
                v-model="printForm.classIDs"
                multiple
                :close-on-select="false"
                :options="printClassOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select class(es)"
                @input="onPrintClassChange"
              append-to-body
            />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="From Date *" label-for="wb-print-from">
              <flat-pickr
                id="wb-print-from"
                v-model="printForm.dtFrom"
                :config="dateConfig"
                class="form-control"
                placeholder="From date (Mon)"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="To Date *" label-for="wb-print-to">
              <flat-pickr
                id="wb-print-to"
                v-model="printForm.dtTo"
                :config="dateConfig"
                class="form-control"
                placeholder="To date (Sat)"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Session" label-for="wb-print-session">
              <v-select
                id="wb-print-session"
                v-model="printForm.sessionID"
                :options="[{ id: 0, session: 'All Sessions' }, ...sessions]"
                :clearable="false"
                label="session"
                :reduce="(val) => val.id"
                placeholder="Select session"
              append-to-body
            />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showPrintModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="goToPrint">
          <feather-icon icon="PrinterIcon" class="mr-50" />
          Print Preview
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormTextarea,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import axios from "axios";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import teacherPlannerScopeMixin from "@/views/myskool/planner/teacherPlannerScopeMixin";

function toDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).split("T")[0];
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toDisplayDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Current week Monday–Saturday (local dates as Y-m-d). */
function getCurrentWeekMonSat(baseDate = new Date()) {
  const d = new Date(baseDate);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diffToMon = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMon);
  const saturday = new Date(monday);
  saturday.setDate(monday.getDate() + 5);
  return {
    dtFrom: toDateInput(monday),
    dtTo: toDateInput(saturday),
  };
}

export default {
  name: "WeeklyBreakup",
  mixins: [teacherPlannerScopeMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormTextarea,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BSpinner,
    BBadge,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      items: [],
      classes: [],
      classesRaw: [],
      sessions: [],
      filterSections: [],
      filterSubjects: [],
      formSections: [],
      formSubjects: [],
      searchQuery: "",
      searchTimeout: null,
      showModal: false,
      isEdit: false,
      isView: false,
      loading: false,
      dataLoading: true,
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      dateConfig: {
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d M Y",
        allowInput: true,
        static: false,
        appendTo: typeof document !== "undefined" ? document.body : undefined,
      },
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "className", label: "Class" },
        { key: "sectionName", label: "Section" },
        { key: "subjectName", label: "Subject" },
        { key: "dateRange", label: "Week" },
        { key: "sessionName", label: "Session" },
        { key: "topics", label: "Topics" },
        { key: "publishDate", label: "Published" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      form: {
        id: 0,
        classID: null,
        sectionID: [],
        subjectID: null,
        dtFrom: "",
        dtTo: "",
        sessionID: null,
        topics: "",
        tID: null,
      },
      filters: {
        classID: null,
        sectionID: [],
        subjectID: null,
        tID: null,
        dtFrom: "",
        dtTo: "",
      },
      showPublishModal: false,
      showPrintModal: false,
      publishLoading: false,
      publishSections: [],
      publishForm: {
        classIDs: [0],
        sectionID: 0,
        dtFrom: "",
        dtTo: "",
        publishDate: "",
      },
      printForm: {
        classIDs: [0],
        dtFrom: "",
        dtTo: "",
        sessionID: 0,
      },
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    printClassOptions() {
      return [{ id: 0, name: "All Classes" }, ...(this.classes || [])];
    },
    canSelectPublishSection() {
      const ids = this.publishForm.classIDs || [];
      return ids.length === 1 && Number(ids[0]) !== 0;
    },
    modalTitle() {
      if (this.isView) return "View Weekly Breakup";
      if (this.isEdit) return "Edit Weekly Breakup";
      return "Add Weekly Breakup";
    },
    viewLabels() {
      const className =
        this.classes.find((c) => Number(c.id) === Number(this.form.classID))
          ?.name || "-";
      const sectionName =
        this.formSections.find(
          (s) => Number(s.id) === Number(this.form.sectionID)
        )?.section || "-";
      const subjectName =
        this.formSubjects.find(
          (s) => Number(s.id) === Number(this.form.subjectID)
        )?.subject || "-";
      const sessionName = this.getSessionName(this.form.sessionID) || "-";
      return { className, sectionName, subjectName, sessionName };
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  async created() {
    if (!this.initPageRights()) return;

    await this.initTeacherPlannerScope();
    await Promise.all([this.loadClasses(), this.loadSessions()]);
    if (!this.isTeacherScoped) {
      await this.loadAllPlannerSubjects();
      this.filterSubjects = this.allSubjects;
      this.formSubjects = this.allSubjects;
    }
    await this.loadItems();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    initPageRights() {
      const right = {};
      (this.$store.state.menu || []).forEach((el) => {
        (el.children || []).forEach((ch) => {
          right[ch.route] = {
            view: ch.view,
            add: ch.add,
            edit: ch.edit,
            delete: ch.delete,
            assignSections: ch.assignSections,
          };
        });
      });
      this.$store.commit("setRights", right);

      if (!this.$store.state.rights[this.$route.name]) {
        this.$router.replace({ name: "misc-not-authorized" });
        return false;
      }
      this.rights = this.$store.state.rights[this.$route.name] || {};
      if (!this.rights.view) {
        this.$router.replace({ name: "misc-not-authorized" });
        return false;
      }
      return true;
    },

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },

    getSessionName(sessionID) {
      if (!sessionID) return "";
      const found = this.sessions.find(
        (s) => Number(s.id) === Number(sessionID)
      );
      return found ? found.session : "";
    },

    async loadSessions() {
      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        const url = `${this.$store.state.domain}Sessions?db=${dbName}&cID=${cID}`;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const data =
          (response && response.data && Array.isArray(response.data)
            ? response.data
            : null) || (Array.isArray(response) ? response : []);
        this.sessions = data;
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },

    formatDateRange(item) {
      const from = toDisplayDate(item.dtFrom);
      const to = toDisplayDate(item.dtTo);
      if (!from && !to) return "-";
      if (from && to) return `${from} – ${to}`;
      return from || to;
    },

    formatDisplayDate(value) {
      return toDisplayDate(value);
    },

    isItemPublished(item) {
      if (!item) return false;
      if (item.isPublished === true || item.published === true) return true;
      return !!(item.publishDate || item.publishedDate || item.dtPublish);
    },

    getItemPublishDate(item) {
      return (
        item?.publishDate ||
        item?.publishedDate ||
        item?.dtPublish ||
        ""
      );
    },

    buildPublishBody() {
      const classIDs = Array.isArray(this.publishForm.classIDs)
        ? this.publishForm.classIDs
        : [];
      const normalized = classIDs.includes(0)
        ? [0]
        : classIDs.filter((id) => Number(id) !== 0);
      const sectionID = this.canSelectPublishSection
        ? this.publishForm.sectionID === null ||
          this.publishForm.sectionID === undefined
          ? 0
          : this.publishForm.sectionID
        : 0;

      return {
        // multi + All Classes (0)
        classIDs: normalized.join(","),
        // backward compatible single field
        classID: normalized.length === 1 ? normalized[0] : 0,
        sectionID,
        dtFrom: toDateInput(this.publishForm.dtFrom),
        dtTo: toDateInput(this.publishForm.dtTo),
        publishDate: toDateInput(this.publishForm.publishDate),
      };
    },

    validatePublishForm() {
      const classIDs = Array.isArray(this.publishForm.classIDs)
        ? this.publishForm.classIDs
        : [];
      if (!classIDs.length) {
        this.$bvToast.toast("Please select at least one class!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return false;
      }
      if (!this.publishForm.dtFrom || !this.publishForm.dtTo) {
        this.$bvToast.toast("Please fill week dates!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return false;
      }
      if (!this.publishForm.publishDate) {
        this.$bvToast.toast("Please select publish date!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return false;
      }
      return true;
    },

    openPublishModal() {
      const week = getCurrentWeekMonSat();
      const today = toDateInput(new Date());
      const classIDs =
        this.filters.classID != null && this.filters.classID !== ""
          ? [this.filters.classID]
          : [0];

      this.publishForm = {
        classIDs,
        sectionID: this.filters.sectionID || 0,
        dtFrom: week.dtFrom,
        dtTo: week.dtTo,
        publishDate: today,
      };
      this.refreshPublishSections();
      if (!this.canSelectPublishSection) {
        this.publishForm.sectionID = 0;
      }
      this.showPublishModal = true;
    },

    onPublishClassChange(classIDs) {
      const ids = Array.isArray(classIDs) ? [...classIDs] : [];
      if (ids.length && Number(ids[ids.length - 1]) === 0) {
        this.publishForm.classIDs = [0];
      } else {
        this.publishForm.classIDs = ids.filter((id) => Number(id) !== 0);
      }
      this.publishForm.sectionID = 0;
      this.refreshPublishSections();
    },

    refreshPublishSections() {
      if (this.canSelectPublishSection) {
        const classID = this.publishForm.classIDs[0];
        this.publishSections = [
          { id: 0, section: "All sections" },
          ...this.getClassSections(classID),
        ];
      } else {
        this.publishSections = [{ id: 0, section: "All sections" }];
      }
    },

    resetPublishForm() {
      this.publishLoading = false;
      this.publishSections = [{ id: 0, section: "All sections" }];
      this.publishForm = {
        classIDs: [0],
        sectionID: 0,
        dtFrom: "",
        dtTo: "",
        publishDate: "",
      };
    },

    openPrintModal() {
      const week = getCurrentWeekMonSat();
      this.printForm = {
        classIDs:
          this.filters.classID != null && this.filters.classID !== ""
            ? [this.filters.classID]
            : [0],
        dtFrom: this.filters.dtFrom ? toDateInput(this.filters.dtFrom) : week.dtFrom,
        dtTo: this.filters.dtTo ? toDateInput(this.filters.dtTo) : week.dtTo,
        sessionID: 0,
      };
      this.showPrintModal = true;
    },

    onPrintClassChange(classIDs) {
      const ids = Array.isArray(classIDs) ? [...classIDs] : [];
      if (ids.length && Number(ids[ids.length - 1]) === 0) {
        this.printForm.classIDs = [0];
      } else {
        this.printForm.classIDs = ids.filter((id) => Number(id) !== 0);
      }
    },

    resetPrintForm() {
      this.printForm = {
        classIDs: [0],
        dtFrom: "",
        dtTo: "",
        sessionID: 0,
      };
    },

    goToPrint() {
      const classIDs = Array.isArray(this.printForm.classIDs)
        ? this.printForm.classIDs
        : [];
      if (!classIDs.length) {
        this.$bvToast.toast("Please select at least one class!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }
      if (!this.printForm.dtFrom || !this.printForm.dtTo) {
        this.$bvToast.toast("Please select week date range!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      const className = classIDs.includes(0)
        ? "All Classes"
        : classIDs
            .map(
              (id) =>
                this.classes.find((c) => Number(c.id) === Number(id))?.name || ""
            )
            .filter(Boolean)
            .join(", ");
      const campus =
        this.$store.state.currentBranch ||
        this.$store.state.branches?.find(
          (b) => Number(b.id) === Number(this.plannerCampusID)
        ) ||
        {};

      const route = this.$router.resolve({
        name: "weekly-breakup-print",
        query: {
          db: this.$store.state.userData.db,
          campusID: this.plannerCampusID,
          classIDs: classIDs.join(","),
          dtFrom: toDateInput(this.printForm.dtFrom),
          dtTo: toDateInput(this.printForm.dtTo),
          sessionID: this.printForm.sessionID || 0,
          sessionName: this.getSessionName(this.printForm.sessionID) || "",
          className,
          campusName:
            campus.name || campus.branch || this.$store.state.branchName || "",
          campusTagline: campus.tagline || campus.slogan || "",
          logo: this.$store.state.logo || campus.logo || "",
          tID: this.isTeacherScoped ? this.teacherScopeTID || "" : "",
          teacherName: this.isTeacherScoped
            ? this.$store.state.userData.name ||
              this.$store.state.userData.fullName ||
              ""
            : "",
        },
      });
      window.open(route.href, "_blank");
      this.showPrintModal = false;
    },

    async publishWeek() {
      if (!this.validatePublishForm()) return;
      this.publishLoading = true;
      try {
        const { data } = await axios({
          method: "post",
          url:
            this.$store.state.domain +
            "WeeklyBreakup/Publish?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.plannerCampusID +
            "&userID=" +
            this.getCurrentUserLinkID(),
          headers: {
            Authorization: "bearer " + this.$store.state.userData.token,
          },
          data: this.buildPublishBody(),
        });

        if (data.status === "success") {
          this.$bvToast.toast("Weekly breakup published successfully.", {
            title: "Success",
            variant: "success",
            solid: true,
          });
          this.showPublishModal = false;
          this.invalidateTeacherScopedCache();
          this.loadItems();
          return;
        }

        this.$bvToast.toast(
          data.message || data.data || "Request failed.",
          {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          }
        );
      } catch (error) {
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.data ||
          error?.message ||
          "Request failed.";
        this.$bvToast.toast(apiMessage, {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.publishLoading = false;
      }
    },

    formatTopicsPreview(topics) {
      if (!topics) return "-";
      const lines = String(topics)
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) return "-";
      if (lines.length === 1) return lines[0];
      return `${lines[0]} (+${lines.length - 1})`;
    },

    getClassSections(classID) {
      if (!classID) return [];

      if (this.isTeacherScoped) {
        const classItem = (this.teacherClassesData || []).find((item) => {
          const id = item.cID ?? item.clsID ?? item.classID ?? null;
          return Number(id) === Number(classID);
        });
        const sections = classItem?.secSubjectsData || [];
        return sections.map((sec) => ({
          id: sec.sectionID ?? sec.id,
          section:
            sec.sectionName ||
            sec.section ||
            sec.name ||
            `Section ${sec.sectionID ?? sec.id}`,
        }));
      }

      const raw = this.classesRaw.find(
        (el) => Number(el?.cls?.id ?? el?.id) === Number(classID)
      );
      const sections =
        raw?.cls?.sections ||
        raw?.sections ||
        this.classes.find((c) => Number(c.id) === Number(classID))?.sections ||
        [];
      return Array.isArray(sections)
        ? sections.map((sec) => ({
            id: sec.id,
            section: sec.section || sec.name || `Section ${sec.id}`,
          }))
        : [];
    },

    async loadClasses() {
      if (this.isTeacherScoped) {
        this.classes = await this.loadTeacherClasses();
        this.classesRaw = this.teacherClassesData || [];
        return;
      }

      const obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.plannerCampusID +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      const classesData = await this.get(obj);
      const list = classesData?.data || classesData || [];
      this.classesRaw = Array.isArray(list) ? list : [];
      this.classes = this.classesRaw.map((el) => el.cls).filter(Boolean);
    },

    buildListRequest(page, pageSize) {
      let url =
        this.$store.state.domain +
        "WeeklyBreakup/GetByPagination?db=" +
        this.$store.state.userData.db +
        "&campusID=" +
        this.plannerCampusID +
        "&page=" +
        page +
        "&pageSize=" +
        pageSize;

      const scopeParts = [];
      this.appendTeacherScopeQueryParts(
        scopeParts,
        this.filters.classID || null
      );
      url += "&" + scopeParts.join("&");

      url +=
        "&sectionID=" +
        (Array.isArray(this.filters.sectionID) ? this.filters.sectionID.join(",") : this.filters.sectionID || "") +
        "&subjectID=" +
        (this.filters.subjectID || "") +
        "&dtFrom=" +
        (this.filters.dtFrom || "") +
        "&dtTo=" +
        (this.filters.dtTo || "");

      if (!this.isTeacherScoped) {
        url += "&tID=";
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        url += "&search=" + encodeURIComponent(this.searchQuery.trim());
      }

      return {
        url,
        token: this.$store.state.userData.token,
        dashboard: true,
      };
    },

    async loadItems(page) {
      if (page) {
        this.currentPage = page;
      }
      this.dataLoading = true;
      try {
        if (this.isTeacherScoped) {
          const cacheKey = [
            this.filters.classID,
            this.filters.sectionID,
            this.filters.subjectID,
            this.filters.dtFrom,
            this.filters.dtTo,
            this.searchQuery,
            this.assignedClassIDs.join(","),
          ].join("|");
          const allItems = await this.fetchTeacherScopedPaginatedData({
            cacheKey,
            fetchPage: (p, ps) => this.get(this.buildListRequest(p, ps)),
          });
          const { items, totalCount } = this.getTeacherScopedPageSlice(allItems);
          this.items = items;
          this.totalCount = totalCount;
        } else {
          const res = await this.get(
            this.buildListRequest(this.currentPage, this.perPage)
          );
          if (res && Array.isArray(res.data)) {
            this.items = res.data;
            this.totalCount = res.totalCount || res.total || res.data.length;
          } else if (Array.isArray(res)) {
            this.items = res;
            this.totalCount = res.length;
          } else {
            this.items = [];
            this.totalCount = 0;
          }
        }
      } finally {
        this.dataLoading = false;
      }
    },

    async onFilterClassChange(classID) {
      this.invalidateTeacherScopedCache();
      this.filters.sectionID = [];
      this.filters.subjectID = null;
      this.filterSections = this.getClassSections(classID);
      this.filterSubjects = this.updateScopedSubjects(classID);
      this.currentPage = 1;
      this.loadItems();
    },

    onFilterChange() {
      this.invalidateTeacherScopedCache();
      this.currentPage = 1;
      this.loadItems();
    },

    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.invalidateTeacherScopedCache();
        this.currentPage = 1;
        this.loadItems();
      }, 500);
    },

    onFormClassChange(classID) {
      this.form.sectionID = [];
      this.form.subjectID = null;
      this.formSections = this.getClassSections(classID);
      this.formSubjects = this.updateScopedSubjects(classID);
    },

    openAddModal() {
      this.isEdit = false;
      this.isView = false;
      this.resetForm();
      this.showModal = true;
    },

    switchToEdit() {
      this.isView = false;
      this.isEdit = true;
    },

    async viewItem(item) {
      this.isView = true;
      this.isEdit = false;
      await this.loadItemIntoForm(item);
    },

    async editItem(item) {
      this.isView = false;
      this.isEdit = true;
      await this.loadItemIntoForm(item);
    },

    async loadItemIntoForm(item) {
      this.loading = true;
      try {
        let data = item;
        try {
          const res = await this.get({
            url:
              this.$store.state.domain +
              "WeeklyBreakup/Load?db=" +
              this.$store.state.userData.db +
              "&campusID=" +
              this.plannerCampusID +
              "&classID=" +
              (item.classID || "") +
              "&sectionID=" +
              (item.sectionID || "") +
              "&subjectID=" +
              (item.subjectID || "") +
              "&tID=" +
              (item.tID || "") +
              "&dtFrom=" +
              toDateInput(item.dtFrom) +
              "&dtTo=" +
              toDateInput(item.dtTo),
            token: this.$store.state.userData.token,
            dashboard: true,
          });
          const list = res?.data || res || [];
          if (Array.isArray(list) && list.length) {
            data =
              list.find((el) => Number(el.id) === Number(item.id)) || list[0];
          } else if (list && !Array.isArray(list) && list.id) {
            data = list;
          }
        } catch (loadErr) {
          console.warn("WeeklyBreakup Load fallback to row item", loadErr);
        }

        this.form = {
          id: data.id || item.id,
          classID: data.classID ?? null,
          sectionID: data.sectionID ? String(data.sectionID).split(',').map(Number).filter(Boolean) : [],
          subjectID: data.subjectID ?? null,
          dtFrom: toDateInput(data.dtFrom),
          dtTo: toDateInput(data.dtTo),
          sessionID: data.sessionID ?? null,
          topics: data.topics || "",
          tID: this.isTeacherScoped
            ? this.teacherScopeTID
            : data.tID || 0,
        };
        this.formSections = this.getClassSections(this.form.classID);
        this.formSubjects = this.updateScopedSubjects(this.form.classID);
        this.showModal = true;
      } catch (error) {
        console.error("Error loading weekly breakup:", error);
        this.$bvToast.toast("Failed to load record", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    async deleteItem(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (!result.isConfirmed) return;

      const obj = {
        url:
          this.$store.state.domain +
          "WeeklyBreakup/Delete?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.plannerCampusID +
          "&id=" +
          item.id +
          "&userID=" +
          this.getCurrentUserLinkID(),
        message: "Weekly breakup deleted successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };
      const success = await this.delete(obj);
      if (success) {
        this.invalidateTeacherScopedCache();
        this.loadItems();
      }
    },

    async saveItem() {
      if (
        !this.form.classID ||
        !this.form.sectionID ||
        (Array.isArray(this.form.sectionID) && !this.form.sectionID.length) ||
        !this.form.subjectID ||
        !this.form.dtFrom ||
        !this.form.dtTo ||
        !this.form.sessionID ||
        !String(this.form.topics || "").trim()
      ) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;
      const body = {
        id: this.form.id || 0,
        classID: this.form.classID,
        sectionID: Array.isArray(this.form.sectionID) ? this.form.sectionID.join(",") : this.form.sectionID,
        subjectID: this.form.subjectID,
        dtFrom: toDateInput(this.form.dtFrom),
        dtTo: toDateInput(this.form.dtTo),
        sessionID: this.form.sessionID,
        topics: String(this.form.topics || "").trim(),
        tID: this.isTeacherScoped ? this.teacherScopeTID || 0 : 0,
      };

      const obj = {
        url:
          this.$store.state.domain +
          "WeeklyBreakup/Save?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.plannerCampusID +
          "&userID=" +
          this.getCurrentUserLinkID(),
        body,
        message:
          "Weekly breakup " +
          (this.isEdit ? "updated" : "created") +
          " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = await this.post(obj);
      if (success) {
        this.showModal = false;
        this.invalidateTeacherScopedCache();
        this.loadItems();
      }
      this.loading = false;
    },

    resetForm() {
      this.isEdit = false;
      this.isView = false;
      this.form = {
        id: 0,
        classID: null,
        sectionID: [],
        subjectID: null,
        dtFrom: "",
        dtTo: "",
        sessionID: null,
        topics: "",
        tID: this.isTeacherScoped ? this.teacherScopeTID || 0 : 0,
      };
      this.formSections = [];
      this.formSubjects = this.isTeacherScoped ? [] : this.allSubjects;
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadItems();
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/myskool/planner/plannerListTable.scss";

.topics-preview {
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.breakup-view {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.breakup-view__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #e5e7eb;
}

.breakup-view__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  min-width: 0;
  background: #fff;
}

.breakup-view__label {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.breakup-view__value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  text-align: right;
  word-break: break-word;
}

.breakup-view__section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.breakup-view__section-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.breakup-view__section-text {
  padding: 0.95rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
  color: #334155;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 110px;
  max-height: 280px;
  overflow-y: auto;
}

.breakup-edit-outline-btn {
  background: #fff !important;
  border: 1.5px solid #7c5dfa !important;
  color: #7c5dfa !important;
  font-weight: 600;

  &:hover {
    background: #f5f3ff !important;
    border-color: #6d4cdb !important;
    color: #6d4cdb !important;
  }
}

@media (max-width: 768px) {
  .topics-preview {
    max-width: 160px;
  }

  .breakup-view__meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .topics-preview {
    max-width: 120px;
  }
}

.breakup-skeleton {
  width: 100%;
  padding: 0.35rem 0.25rem 0.75rem;
}

.breakup-skeleton__row {
  display: grid;
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.breakup-skeleton__cell {
  min-width: 0;
}

.breakup-skeleton__bar {
  height: 14px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #ecebf8 0%,
    #f7f6ff 40%,
    #ecebf8 80%
  );
  background-size: 200% 100%;
  animation: breakupSkeletonShine 1.2s ease-in-out infinite;
}

@keyframes breakupSkeletonShine {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

::v-deep .filter-fields-group .filter-search-input {
  flex: 1 1 120px !important;
  min-width: 120px !important;
  max-width: 180px !important;
}

::v-deep .filter-control.vs--multiple .vs__selected {
  white-space: nowrap;
}

::v-deep .filter-control.vs--multiple .vs__selected-options {
  flex-wrap: nowrap;
  overflow: hidden;
  align-items: center;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/*
 * Scoped to Weekly Breakup modals only — do NOT use body.modal-open globally
 * (that breaks Planner Concepts / Topics modals).
 * z-index above page-size dropdown (99999 in plannerListTable.scss).
 */
.modal.breakup-form-modal {
  z-index: 110000 !important;
}

.modal-backdrop.breakup-form-backdrop {
  z-index: 109990 !important;
}

body:has(.modal.breakup-form-modal.show) .vs__dropdown-menu {
  z-index: 110010 !important;
}

body:has(.modal.breakup-form-modal.show) .flatpickr-calendar {
  z-index: 110020 !important;
}
</style>
